/*@ts-expect-error untyped module */
import localeComparator from 'I18n/utils/localeComparator';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CRMPropertyValueTypes from 'automation-ui-flow-core/fields/CRMPropertyValueTypes';
// @ts-expect-error untyped module
import LanguageSelect from 'ui-addon-i18n/components/LanguageSelect';
import getLangLocale from 'I18n/utils/getLangLocale';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import { jsx as _jsx } from "react/jsx-runtime";
// List of functions that are available in simple mode
// NOTE(bheng): This needs to be synced with getSupportedHubLFilterConfigs keys.
//   We can't just use Object.keys() on the configs because they contain I18n
//   strings that need to wait for initialization.
export const FormulaFunctionNames = ['abs', 'add', 'capitalize', 'cut', 'divide', 'divisible', 'format_datetime', 'lower', 'minus_time', 'multiply', 'plus_time', 'replace', 'root', 'round', 'striptags', 'title', 'trim', 'unixtimestamp', 'upper', 'urlencode'];
const makeArgumentDescriptor = ({
  emptyValue = null,
  label,
  description,
  name,
  required,
  type,
  types = [type],
  tooltip,
  help,
  allowCreate,
  enumOptions,
  selectPlaceholder,
  hasVariadicArguments
}) => {
  return {
    emptyValue,
    label,
    description,
    name,
    required,
    /* Primary type for the argument. Used to determine what kind of input to use for entry in simple mode */
    type: type || types[0],
    /* The set of all supported types for this argument. Used in advanced mode. */
    types,
    tooltip,
    help,
    /* Loosen enum validation since we can't validate custom values. */
    allowCreate,
    enumOptions,
    selectPlaceholder,
    hasVariadicArguments
  };
};

/*
  Label: Short label, for example a form field label
  Description: A longer, period terminated, explanation of the purpose of the function. For example, function documentation

  The conventions above apply for argument labels and descriptions as well.
*/

export const getFormulaFunctionDefinitions = () => ({
  abs: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.abs.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.abs.description'),
    examples: [{
      formulaText: 'abs(-10)',
      output: 10
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.abs.arguments.number.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    })],
    metadataInputs: [],
    returnType: CRMPropertyValueTypes.NUMBER,
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER, CRMPropertyValueTypes.STRING]
  },
  add: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.add.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.add.description'),
    examples: [{
      formulaText: 'add(1, 1)',
      output: 2
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.add.arguments.number.description'),
      label: null,
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.add.arguments.numberToAdd.description'),
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.add.arguments.numberToAdd.label'),
      name: 'number_to_add',
      required: true,
      type: CRMPropertyValueTypes.NUMBER
    })],
    metadataInputs: [],
    returnType: CRMPropertyValueTypes.NUMBER,
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER, CRMPropertyValueTypes.STRING]
  },
  concat: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.concat.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.concat.description'),
    isCustom: true,
    examples: [{
      formulaText: 'concat("Hello ", "world", "!")',
      output: 'Hello world!'
    }, {
      formulaText: 'concat([firstname], " ",\n[lastname])' /* line break for text wrapping readability */,
      context: {
        firstname: propertyLabelTranslator('First name'),
        lastname: propertyLabelTranslator('Last name')
      },
      output: 'John Smith'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'items',
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.concat.arguments.items.description'),
      types: [CRMPropertyValueTypes.STRING, CRMPropertyValueTypes.NUMBER],
      required: true,
      hasVariadicArguments: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  capitalize: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.capitalize.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.capitalize.description'),
    examples: [{
      formulaText: 'capitalize("hello world")',
      output: 'Hello world'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.capitalize.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    metadataInputs: [],
    returnType: CRMPropertyValueTypes.STRING,
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  cut: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.cut.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.cut.description'),
    examples: [{
      formulaText: 'cut("Hello world.", " world")',
      output: 'Hello.'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'source',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.cut.arguments.source.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'characters_to_cut',
      help: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.cut.arguments.characters_to_cut.help'),
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.cut.arguments.characters_to_cut.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.cut.arguments.characters_to_cut.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true,
      emptyValue: ''
    })],
    metadataInputs: [],
    returnType: CRMPropertyValueTypes.STRING,
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  datetimeformat: {
    deprecatedBy: 'format_datetime',
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.description'),
    examples: [{
      formulaText: 'datetimeformat([closedate], "%B %e %Y %l %p", "America/Los_Angeles")',
      context: {
        closedate: propertyLabelTranslator('Close date')
      },
      output: 'October 1 2020 9 AM'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'datetime',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.datetime.description'),
      type: CRMPropertyValueTypes.DATETIME,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.format.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.format.description'),
      name: 'format',
      required: true,
      type: CRMPropertyValueTypes.STRING
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.timezone.label"
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.timezone.description_jsx",
        elements: {
          Link: UILink
        },
        options: {
          href: 'https://docs.oracle.com/javase/8/docs/api/java/util/TimeZone.html'
        }
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.timezone.description_jsx",
        elements: {
          Link: UILink
        },
        options: {
          href: 'https://docs.oracle.com/javase/8/docs/api/java/util/TimeZone.html'
        }
      }),
      name: 'timezone',
      required: false,
      type: CRMPropertyValueTypes.STRING
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [{
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.datetimeformat.arguments.language.label"
      }),
      key: 'languageTag',
      getDefaultValue: () => getLangLocale(),
      input: LanguageSelect
    }],
    isCustom: true,
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  divide: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divide.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divide.description'),
    examples: [{
      formulaText: 'divide(4, 2)',
      output: 2
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divide.arguments.number.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divide.arguments.divisor.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divide.arguments.divisor.description'),
      name: 'divisor',
      required: true,
      type: CRMPropertyValueTypes.NUMBER
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER, CRMPropertyValueTypes.STRING]
  },
  divisible: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divisible.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divisible.description'),
    examples: [{
      formulaText: 'divisible(4, 2)',
      output: 'true'
    }, {
      formulaText: 'divisible(5, 2)',
      output: 'false'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divisible.arguments.number.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      help: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divisible.arguments.divisor.help'),
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divisible.arguments.divisor.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.divisible.arguments.divisor.description'),
      name: 'divisor',
      required: true,
      type: CRMPropertyValueTypes.NUMBER
    })],
    returnType: CRMPropertyValueTypes.BOOL,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.BOOL, CRMPropertyValueTypes.STRING]
  },
  format_datetime: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.description'),
    examples: [{
      formulaText: "format_datetime([closedate], 'medium', 'America/New_York', 'de-DE')",
      output: '08.02.2024, 17:09:49'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'date',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.date.description'),
      types: [CRMPropertyValueTypes.DATETIME, CRMPropertyValueTypes.DATE],
      required: true
    }), makeArgumentDescriptor({
      name: 'format',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.format.label'),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.format.description_jsx",
        elements: {
          Link: UILink
        },
        options: {
          href: 'https://unicode.org/reports/tr35/tr35-dates.html#Date_Format_Patterns'
        }
      }),
      types: [CRMPropertyValueTypes.STRING, CRMPropertyValueTypes.ENUM],
      allowCreate: true,
      emptyValue: 'short',
      enumOptions: [{
        value: 'short',
        text: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.format.short')
      }, {
        value: 'medium',
        text: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.format.medium')
      }, {
        value: 'long',
        text: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.format.long')
      }, {
        value: 'full',
        text: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.format.full')
      }],
      required: false
    }), makeArgumentDescriptor({
      name: 'timeZone',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.timezone.label'),
      emptyValue: 'UTC',
      tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.timezone.description_jsx",
        elements: {
          Link: UILink
        },
        options: {
          href: 'https://data.iana.org/time-zones/tzdb/'
        }
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.timezone.description_jsx",
        elements: {
          Link: UILink
        },
        options: {
          href: 'https://data.iana.org/time-zones/tzdb/'
        }
      }),
      types: [CRMPropertyValueTypes.STRING],
      required: false
    }), makeArgumentDescriptor({
      name: 'locale',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.locale.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.formatDatetime.arguments.locale.description'),
      types: [CRMPropertyValueTypes.STRING],
      required: false
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  join: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.join.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.join.description'),
    examples: [{
      formulaText: 'join(" - ", "A", "B", "C")',
      output: 'A - B - C'
    }],
    isCustom: true,
    functionArguments: [makeArgumentDescriptor({
      name: 'separator',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.join.arguments.delimiter.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'items',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.join.arguments.items.description'),
      types: [CRMPropertyValueTypes.STRING, CRMPropertyValueTypes.NUMBER],
      required: true,
      hasVariadicArguments: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  lower: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.lower.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.lower.description'),
    examples: [{
      formulaText: 'lower("Hello World!")',
      output: 'hello world!'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.lower.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  length: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.length.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.length.description'),
    examples: [{
      formulaText: 'length("Hello World!")',
      output: 12
    }],
    isCustom: true,
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.length.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER]
  },
  minus_time: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.description'),
    examples: [{
      formulaText: 'minus_time([closedate], 2, "days")',
      context: {
        closedate: propertyLabelTranslator('Close date')
      }
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'datetime',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.arguments.datetime.description'),
      type: CRMPropertyValueTypes.DATETIME,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.arguments.diff.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.arguments.diff.description'),
      name: 'diff',
      required: true,
      type: 'number'
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.arguments.timeunit.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.minus_time.arguments.timeunit.description'),
      name: 'timeunit',
      required: true,
      enumOptions: [{
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.seconds'),
        value: 'seconds'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.minutes'),
        value: 'minutes'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.hours'),
        value: 'hours'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.half-days'),
        value: 'half_days'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.days'),
        value: 'days'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.weeks'),
        value: 'weeks'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.months'),
        value: 'months'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.years'),
        value: 'years'
      }],
      selectPlaceholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.placeholder"
      }),
      type: CRMPropertyValueTypes.STRING
    })],
    returnType: CRMPropertyValueTypes.DATETIME,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.DATE, CRMPropertyValueTypes.DATETIME]
  },
  multiply: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.multiply.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.multiply.description'),
    examples: [{
      formulaText: 'multiply(3, 3)',
      output: 9
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.multiply.arguments.number.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.multiply.arguments.multiplier.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.multiply.arguments.multiplier.description'),
      name: 'multiplier',
      required: true,
      type: CRMPropertyValueTypes.NUMBER
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER, CRMPropertyValueTypes.STRING]
  },
  number: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.number.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.number.description'),
    examples: [{
      formulaText: 'number("36")',
      output: 36
    }],
    isCustom: true,
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.number.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'default',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.number.arguments.default.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: false
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER]
  },
  plus_time: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.description'),
    examples: [{
      formulaText: 'plus_time([closedate], 2, "days")',
      context: {
        closedate: propertyLabelTranslator('Close date')
      }
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'datetime',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.arguments.datetime.description'),
      type: CRMPropertyValueTypes.DATETIME,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.arguments.diff.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.arguments.diff.description'),
      name: 'diff',
      required: true,
      type: CRMPropertyValueTypes.NUMBER
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.arguments.timeunit.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.plus_time.arguments.timeunit.description'),
      name: 'timeunit',
      required: true,
      enumOptions: [{
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.seconds'),
        value: 'seconds'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.minutes'),
        value: 'minutes'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.hours'),
        value: 'hours'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.half-days'),
        value: 'half_days'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.days'),
        value: 'days'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.weeks'),
        value: 'weeks'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.months'),
        value: 'months'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.options.years'),
        value: 'years'
      }],
      selectPlaceholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-formula-editor.formula.hubl-filter-params.unit-of-time.placeholder"
      }),
      type: CRMPropertyValueTypes.STRING
    })],
    returnType: CRMPropertyValueTypes.DATETIME,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.DATE, CRMPropertyValueTypes.DATETIME, CRMPropertyValueTypes.STRING]
  },
  randomNumber: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.randomNumber.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.randomNumber.description'),
    examples: [{
      formulaText: 'randomNumber(0, 100)',
      output: '42'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'minimum',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.randomNumber.arguments.minimum.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      name: 'maximum',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.randomNumber.arguments.maximum.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER]
  },
  regex_replace: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.regex_replace.label'),
    description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "automation-ui-formula-editor.formula.functionDefinitions.regex_replace.description_jsx",
      elements: {
        Link: UILink
      },
      options: {
        href: 'https://github.com/google/re2/wiki/Syntax'
      }
    }),
    isCustom: true,
    examples: [{
      formulaText: 'regex_replace("contact-us-2", "[^a-zA-Z]", "")',
      output: 'contactus'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'original_string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.regex_replace.arguments.original_string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'pattern',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.regex_replace.arguments.pattern.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'replacement_string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.regex_replace.arguments.replacement_string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  replace: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.description'),
    examples: [{
      formulaText: 'replace("Hello world!", "Hello", "Hi")',
      output: 'Hi world!'
    }, {
      formulaText: 'replace("hip hip hooray", "hip", "hop", 1)',
      output: 'hop hip hooray'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.old.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.old.description'),
      name: 'old',
      required: true,
      type: CRMPropertyValueTypes.STRING
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.new.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.new.description'),
      name: 'new',
      required: true,
      type: CRMPropertyValueTypes.STRING
    }), makeArgumentDescriptor({
      emptyValue: null,
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.count.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.count.description'),
      tooltip: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.replace.arguments.count.tooltip'),
      name: 'count',
      required: false,
      type: CRMPropertyValueTypes.NUMBER
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  root: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.root.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.root.description'),
    examples: [{
      formulaText: 'root(16)',
      output: 4
    }, {
      formulaText: 'root(625, 4)',
      output: 5
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.root.arguments.number.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.root.arguments.nthRoot.description'),
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.root.arguments.nthRoot.label'),
      name: 'nth_root',
      required: false,
      type: CRMPropertyValueTypes.NUMBER
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    additionalInputs: [],
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER, CRMPropertyValueTypes.STRING]
  },
  round: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.description'),
    examples: [{
      formulaText: 'round(52.5)',
      output: 53
    }, {
      formulaText: "round(52.5, 0, 'floor')",
      output: 52
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'number',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.arguments.number.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      emptyValue: null,
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.arguments.precision.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.arguments.precision.description'),
      name: 'precision',
      required: false,
      enumOptions: [{
        text: I18n.formatNumber(1),
        value: 0
      }, {
        text: I18n.formatNumber(0.1),
        value: 1
      }, {
        text: I18n.formatNumber(0.01),
        value: 2
      }, {
        text: I18n.formatNumber(0.001),
        value: 3
      }],
      selectPlaceholder: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.arguments.precision.placeholder'),
      type: CRMPropertyValueTypes.NUMBER
    }), makeArgumentDescriptor({
      emptyValue: '',
      label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.arguments.roundingMethod.label'),
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.round.arguments.roundingMethod.description'),
      name: 'rounding_method',
      required: false,
      enumOptions: [{
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.rounding-method.options.common'),
        value: 'common'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.rounding-method.options.ceil'),
        value: 'ceil'
      }, {
        text: I18n.text('automation-ui-formula-editor.formula.hubl-filter-params.rounding-method.options.floor'),
        value: 'floor'
      }],
      selectPlaceholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-formula-editor.formula.hubl-filter-params.rounding-method.placeholder"
      }),
      type: CRMPropertyValueTypes.STRING
    })],
    returnType: CRMPropertyValueTypes.NUMBER,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.NUMBER, CRMPropertyValueTypes.STRING]
  },
  striptags: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.striptags.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.striptags.description'),
    examples: [{
      formulaText: 'striptags("<div><strong>My text</strong></div>")',
      output: 'My text'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.striptags.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  left: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.left.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.left.description'),
    examples: [{
      formulaText: "left('hello world.', 5)",
      output: 'hello'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.left.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'length',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.left.arguments.length?.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: false
    })],
    isCustom: true,
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  right: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.right.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.right.description'),
    examples: [{
      formulaText: "right('hello world.', 6)",
      output: 'world.'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.right.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'length',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.right.arguments.length?.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: false
    })],
    isCustom: true,
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  mid: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.mid.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.mid.description'),
    examples: [{
      formulaText: "mid('hello world.', 1, 4)",
      output: 'ello'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.mid.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    }), makeArgumentDescriptor({
      name: 'start-index',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.mid.arguments.start-index.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    }), makeArgumentDescriptor({
      name: 'length',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.mid.arguments.length.description'),
      type: CRMPropertyValueTypes.NUMBER,
      required: true
    })],
    isCustom: true,
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  title: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.title.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.title.description'),
    examples: [{
      formulaText: "title('hello world.')",
      output: 'Hello World.'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.title.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  trim: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.trim.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.trim.description'),
    examples: [{
      formulaText: 'trim("   Hello world   ")',
      output: 'Hello world'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.trim.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  unixtimestamp: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.unixtimestamp.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.unixtimestamp.description'),
    examples: [{
      formulaText: 'unixtimestamp([closedate])',
      context: {
        closedate: propertyLabelTranslator('Close date')
      }
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'datetime',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.unixtimestamp.arguments.datetime.description'),
      type: CRMPropertyValueTypes.DATETIME,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING, CRMPropertyValueTypes.DATE, CRMPropertyValueTypes.DATETIME]
  },
  upper: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.upper.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.upper.description'),
    examples: [{
      formulaText: 'upper("hello world")',
      output: 'HELLO WORLD'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.upper.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  },
  urlencode: {
    label: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.urlencode.label'),
    description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.urlencode.description'),
    examples: [{
      formulaText: 'urlencode("Escape & URL encode this string")',
      output: 'Escape+%26+URL+encode+this+string'
    }],
    functionArguments: [makeArgumentDescriptor({
      name: 'string',
      label: null,
      description: I18n.text('automation-ui-formula-editor.formula.functionDefinitions.urlencode.arguments.string.description'),
      type: CRMPropertyValueTypes.STRING,
      required: true
    })],
    returnType: CRMPropertyValueTypes.STRING,
    metadataInputs: [],
    allowedOutputTypes: [CRMPropertyValueTypes.STRING]
  }
});

// Return object of supported filters keyed by expression name
export const getFiltersByExpressionName = () => {
  const supportedFilters = getFormulaFunctionDefinitions();
  return Object.entries(supportedFilters).reduce((acc, [filterId, filter]) => {
    return Object.assign(acc, {
      [filterId]: filter
    });
  }, {});
};
export const getFormulaFunctionsOptions = supportedHubLFilterConfigs => FormulaFunctionNames.map(filterName => ({
  text: supportedHubLFilterConfigs[filterName].label,
  value: filterName
})).sort((optionA, optionB) => localeComparator(optionA.text, optionB.text));